import { useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { tokens } from '@/theme/tokens';

/**
 * The non-visual platform APIs the screens call: alerts, sharing, opening
 * links, and the ambient device state. Same names and shapes as React Native's
 * so the ported screens are unchanged; the implementations are the web's.
 */

/* -------------------------------------------------------------------------- */
/* Alert                                                                      */
/* -------------------------------------------------------------------------- */

export type AlertButton = {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
};

type AlertRequest = {
  id: number;
  title: string;
  message?: string;
  buttons: AlertButton[];
};

let queue: AlertRequest[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

const alertStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot: () => queue,
};

/**
 * `window.alert`/`confirm` would work, but they render as Chrome's own grey
 * dialog — a jarring break from a branded app, blocking, and unstyleable. This
 * pushes onto a queue that <AlertHost/> renders in the app's own sheet.
 */
export const Alert = {
  alert(title: string, message?: string, buttons?: AlertButton[]) {
    queue = [
      ...queue,
      { id: nextId++, title, message, buttons: buttons?.length ? buttons : [{ text: 'OK' }] },
    ];
    emit();
  },
};

function dismiss(id: number) {
  queue = queue.filter((a) => a.id !== id);
  emit();
}

/** Mounted once, near the root. Renders whatever `Alert.alert` has queued. */
export function AlertHost() {
  const alerts = useSyncExternalStore(alertStore.subscribe, alertStore.getSnapshot, alertStore.getSnapshot);
  const current = alerts[0];

  useEffect(() => {
    if (!current) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const cancel = current.buttons.find((b) => b.style === 'cancel');
      cancel?.onPress?.();
      dismiss(current.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current]);

  if (!current) return null;

  const press = (button: AlertButton) => {
    dismiss(current.id);
    button.onPress?.();
  };

  return createPortal(
    <div className="sg-alert-backdrop" role="presentation">
      <div className="sg-alert" role="alertdialog" aria-modal="true" aria-label={current.title}>
        <p className="sg-alert-title">{current.title}</p>
        {current.message ? <p className="sg-alert-message">{current.message}</p> : null}
        <div className="sg-alert-actions">
          {current.buttons.map((button, i) => (
            <button
              key={button.text + i}
              type="button"
              className={'sg-alert-button sg-alert-' + (button.style ?? 'default')}
              autoFocus={i === current.buttons.length - 1}
              onClick={() => press(button)}
            >
              {button.text}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Share / Linking                                                            */
/* -------------------------------------------------------------------------- */

export const Share = {
  /**
   * The Web Share API where the browser has it (every mobile browser worth
   * targeting), clipboard otherwise — a desktop browser has no share sheet, and
   * silently doing nothing is worse than copying the link.
   */
  async share(content: { message?: string; url?: string; title?: string }) {
    const text = content.message;
    const url = content.url;
    if (navigator.share) {
      try {
        await navigator.share({ title: content.title, text, url });
        return { action: 'sharedAction' as const };
      } catch {
        return { action: 'dismissedAction' as const };
      }
    }
    const payload = [text, url].filter(Boolean).join(' ');
    try {
      await navigator.clipboard.writeText(payload);
      Alert.alert(content.title ?? 'Copied', 'Link copied to your clipboard.');
    } catch {
      Alert.alert(content.title ?? 'Share', payload);
    }
    return { action: 'sharedAction' as const };
  },
};

export const Linking = {
  async openURL(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  },
  async canOpenURL() {
    return true;
  },
};

/* -------------------------------------------------------------------------- */
/* Platform / I18nManager / AppState                                          */
/* -------------------------------------------------------------------------- */

export type PlatformOS = 'ios' | 'android' | 'web';

export const Platform = {
  // Typed as the full union, not the literal 'web': the ported screens still
  // carry `Platform.OS === 'ios'` branches, and narrowing the type to one
  // member would make TypeScript reject those as impossible comparisons rather
  // than let them evaluate to false the way they do at runtime.
  OS: 'web' as PlatformOS,
  select<T>(spec: { web?: T; ios?: T; android?: T; native?: T; default?: T }): T | undefined {
    return spec.web ?? spec.default;
  },
};

/**
 * `document.dir` is the web's layout direction and, unlike RN's
 * `I18nManager.forceRTL`, flipping it re-mirrors immediately — no reload, no
 * "restart required" prompt. `setLanguage` in @/i18n relies on that.
 */
export const I18nManager = {
  get isRTL() {
    return typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  },
  allowRTL(_allow: boolean) {
    /* no-op: the browser always allows it */
  },
  forceRTL(rtl: boolean) {
    if (typeof document !== 'undefined') document.documentElement.dir = rtl ? 'rtl' : 'ltr';
  },
};

export type AppStateStatus = 'active' | 'background' | 'inactive';

/** `visibilitychange` is the browser's active/background, near enough for the
 *  polling and refresh-on-return behaviour the screens use it for. */
export const AppState = {
  get currentState(): AppStateStatus {
    return typeof document !== 'undefined' && document.visibilityState === 'visible' ? 'active' : 'background';
  },
  addEventListener(type: 'change', handler: (state: AppStateStatus) => void) {
    if (type !== 'change') return { remove() {} };
    const onChange = () => handler(document.visibilityState === 'visible' ? 'active' : 'background');
    document.addEventListener('visibilitychange', onChange);
    window.addEventListener('focus', onChange);
    return {
      remove() {
        document.removeEventListener('visibilitychange', onChange);
        window.removeEventListener('focus', onChange);
      },
    };
  },
};

/* -------------------------------------------------------------------------- */
/* StatusBar                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Native tints the OS status bar per screen. The browser exposes only one
 * document-level `theme-color`, which ThemeProvider already keeps in step with
 * the scheme — so a per-screen <StatusBar> is a no-op that exists to keep the
 * ported screens byte-comparable with mobile's.
 */
export function StatusBar(_props: { style?: 'light' | 'dark' | 'auto'; backgroundColor?: string }) {
  return null;
}

/* -------------------------------------------------------------------------- */
/* Misc                                                                       */
/* -------------------------------------------------------------------------- */

export const brandColors = tokens;
